/**
 * @file events.ts
 *
 * Central registry of event name constants used across the App Suite.
 * Grouped by functional area.
 */

import type { Project } from './projectManagement.ts';

export const AS_APP_EVENTS = {
  /** Dispatched by <as-app> when connected. Detail: { sender: AsApp } */
  READY: 'as-app',
  /** Dispatched when aggregate busy state changes. Detail: { busy: boolean, keys: string[] } */
  BUSY: 'as-app-busy',
  /** Request to update ref-counted busy state. Detail: { key: string, busy: boolean } */
  BUSY_REQUEST: 'as-app-busy-request',
  /** Trigger a UI notification. Detail: { defaultText, dictKey, type, persist, actions, vars, resolve } */
  NOTIFY: 'as-notify',
  /** Close a persistent notification. Detail: { id: string } */
  CLOSE_NOTIFICATION: 'as-close-notification',
  /** Standard button click event from header/footer. Detail: { buttonName: string } */
  BUTTON_CLICK: 'as-button-click',
  /** Request to open the global modal overlay. Detail: { visibleChild?: string } */
  MODAL_OPEN: 'as-open-modal',
  /** Request to close the global modal overlay. */
  MODAL_CLOSE: 'as-close-modal',
  /** Set the active child ID in the modal overlay. Detail: { id: string } */
  MODAL_SET_VISIBLE_CHILD: 'as-set-visible-child',
  /** Dispatched by the overlay when it finishes closing. */
  MODAL_CLOSED: 'as-modal-closed',
  /** Trigger a temporary visual flash/highlight on an element. Detail: { target: string | HTMLElement } */
  FLASH_ELEMENT: 'as-flash-element',
} as const;

export const AS_PROJECT_EVENTS = {
  /** Dispatched on <as-app> (bubbles) just before a new project replaces the
   *  current one. Detail: { project } — the OUTGOING project, or null. The
   *  session reinit and input resets haven't started yet. */
  BEFORE_NEW: 'as-before-project-new',
  /** Dispatched on <as-app> (bubbles) once the new project is current and the
   *  inputs are reset. Detail: { project } — the new project. */
  AFTER_NEW: 'as-after-project-new',
  //TODO:@REMOVE
  /** @deprecated use `AFTER_NEW` (`as-after-project-new`). Still fired second
   *  with the same detail. */
  NEW: 'as-project-new',
  /** Dispatched before a project load starts, on the <as-app> element (bubbles).
   *  Detail: { project: Project } — the incoming project as it arrived, BEFORE
   *  migrations run, so it may not match what AFTER_LOADED reports. */
  BEFORE_LOADED: 'as-before-project-loaded',
  /** Dispatched after a project is successfully loaded, on the <as-app> element
   *  (bubbles). Detail: { project: Project } — the migrated project that is now
   *  live. Does NOT fire if the load throws; use sysData.projectLoading if you
   *  need a flag that clears either way. */
  AFTER_LOADED: 'as-after-project-loaded',
  //TODO:@REMOVE
  /** @deprecated use `AFTER_LOADED` (`as-after-project-loaded`) instead. Still
   *  dispatched alongside it, with the same detail, and will keep working until
   *  it's removed. Unlike a deprecated method, an event alias can't warn — we
   *  can't see who's listening — so this notice is the only signal you get. */
  LOADED: 'as-project-loaded',
  /** Dispatched on <as-app> (bubbles) before a save collects the app's state —
   *  before the before-save hooks run. Detail: { reason: ProjectSaveReason }. */
  BEFORE_SAVED: 'as-before-project-saved',
  /** Dispatched on <as-app> (bubbles) after the project state is committed.
   *  Detail: { reason: ProjectSaveReason } — 'save' for a real file save,
   *  'autosave' for the debounced in-memory capture. See ProjectSavedDetail /
   *  isRealProjectSave below. */
  AFTER_SAVED: 'as-after-project-saved',
  //TODO:@REMOVE
  /** @deprecated use `AFTER_SAVED` (`as-after-project-saved`). Still fired
   *  second with the same detail. */
  SAVED: 'as-project-saved',
  /** Internal. Dispatched during a load once UI inputs and CAD state are
   *  restored, before AFTER_LOADED. Detail: { customData }. Apps should
   *  listen to AFTER_LOADED instead. */
  SESSION_RESTORED: 'as-intrn-session-restored',
  //TODO:@REMOVE
  /** @deprecated unprefixed name of SESSION_RESTORED, still fired alongside. */
  SESSION_RESTORED_LEGACY: 'session-restored',
  /** Dispatched on <as-app> to ask for a new project (the header's New button).
   *  The unprefixed `new-session` still works but is deprecated. */
  NEW_REQUEST: 'as-new-session',
  /** Dispatched on <as-app> to ask for a save (the header's Save button).
   *  The unprefixed `save-session` still works but is deprecated. */
  SAVE_REQUEST: 'as-save-session',
  /** Dispatched on <as-app> when a .asproject file is dropped/selected. Detail: { blob }.
   *  The unprefixed `restore-session-file` still works but is deprecated. */
  RESTORE_FILE: 'as-restore-session-file',
  /** Window event: reset all inputs to defaults. Fired by managed project handling
   *  (createAndSetNewProject). Detail: { silenceChange?: boolean } */
  INPUTS_RESET: 'as-inputs-reset',
  /** A committed input change that should drive app logic — a user edit, or an
   *  explicit setValue(). Detail carries `reason` (see InputChangeReason). */
  INPUT_CHANGE: 'as-input-change',
  /** A committed input change that should NOT drive app logic — a project load,
   *  an undo, a param sync, a reset, or a quiet `.value =`. Same detail shape.
   *  Listen to this when you want to observe every value change; listen to
   *  INPUT_CHANGE when you want to act on real edits. */
  INPUT_SYNC: 'as-input-sync',
  /** The in-progress phase of a user edit — typing, dragging a slider. */
  INPUT_UPDATE: 'as-input-update',
  /** A value is on its way to the configurator. Detail: { paramName, value } */
  INPUT_PENDING: 'as-input-pending',
  /** The configurator confirmed a value.
   *  Detail: { paramName, value, previousValue } */
  INPUT_RESOLVED: 'as-input-resolved',
  /** An input re-applied a saved value on restore. Detail: { id, value } */
  INPUT_RESTORED: 'as-input-restored',
  /** A trcad-param names a parameter the configurator doesn't have.
   *  Detail: { paramName } */
  INPUT_PARAM_MISSING: 'as-input-param-missing',
  /** Dispatched when appData changes. */
  APP_DATA_CHANGED: 'as-app-data-changed',
  //TODO:@REMOVE
  /** @deprecated use `APP_DATA_CHANGED` instead. Still fired alongside it. */
  CUSTOM_DATA_CHANGED: 'as-custom-data-changed',
} as const;

/** Detail carried by `as-before-project-loaded`, `as-after-project-loaded` and
 *  the deprecated `as-project-loaded`. The before event reports the project as
 *  it arrived; the after events report it as it ended up, which differs
 *  whenever a migration ran. */
export interface ProjectLoadDetail {
  project: Project;
}

/** Why a project save ran: a real file save the user asked for, or the
 *  debounced in-memory autosave capture. Same vocabulary as the before-save
 *  hooks (see BeforeSaveReason in as-app/mixins/project-mixin.ts). */
export type ProjectSaveReason = 'save' | 'autosave';

/** Detail carried by `as-before-project-saved`, `as-after-project-saved` and
 *  the deprecated `as-project-saved`. Autosave fires the same events as a
 *  real save so listeners that just want "the project state is current" (a
 *  status line, a host mirroring customData) get told either way — but an
 *  autosave writes no file, so anything that advances the user off the back
 *  of a save must check `reason` first. */
export interface ProjectSavedDetail {
  reason: ProjectSaveReason;
}

/** True when an `as-after-project-saved` came from a real file save. Use this to
 *  gate anything that closes the app, clears a spinner, or resolves a
 *  save-and-continue flow. A bare event with no detail counts as a real save:
 *  older dispatchers predate this detail. */
export const isRealProjectSave = (e: Event): boolean =>
  (e as CustomEvent<Partial<ProjectSavedDetail> | null>).detail?.reason !== 'autosave';

export const AS_CONFIGURATOR_EVENTS = {
  /** Internal. Dispatched on window once <as-configurator> has bound its
   *  Paramate instance. Detail: { sender: AsConfigurator, configurator }.
   *  Apps should use sysData.appReady or addToPostInitQueue() instead. */
  READY: 'as-intrn-configurator-ready',
  /** Dispatched by Paramate when a session is initialized. */
  PARAMATE_INIT: 'paramate-configurator-initialized',
  /** Dispatched by Paramate before a configuration cycle. */
  PARAMATE_BEFORE_CONFIGURE: 'paramate-configurator-before-configure',
  /** Dispatched by Paramate after a configuration cycle. */
  PARAMATE_AFTER_CONFIGURE: 'paramate-configurator-after-configure',
  /** Dispatched by Paramate after a session restore. */
  PARAMATE_AFTER_RESTORE: 'paramate-configurator-after-restore',
  /** Dispatched when Paramate encounters an error. */
  PARAMATE_ERROR: 'paramate-configurator-error',
  /** Trigger a CAD configuration cycle. Detail: { params?, skipHistory? } */
  CONFIGURE: 'as-configure',
  /** Trigger configure if any values are pending. */
  CONFIGURE_PENDING: 'as-configure-pending',
  /** Internal. as-file-input asks the configurator to upload a file. Detail: { file, fileParamName, resolve, reject, ... } */
  UPLOAD_FILE: 'as-intrn-upload-file',
  /** Internal. as-file-input asks the configurator to delete a file. Detail: { fileParamName } */
  DELETE_FILE: 'as-intrn-delete-file',
  /** Reset the configurator to its initial state. */
  RESET: 'as-reset-configurator',
  /** Session keepalive heartbeat. */
  HEARTBEAT: 'as-heartbeat',
  /** The keepalive found the server session dead (401/404) — e.g. the machine slept
   *  past the session TTL. Detail: { error }. Routed into the expired-session flow. */
  SESSION_LOST: 'as-session-lost',
} as const;

export const AS_MESH_EVENTS = {
  /** Request a mesh download. Detail: { finalized, type, fileHandle } */
  DOWNLOAD_REQUEST: 'as-download-mesh',
  /** Request a mesh blob from the configurator. Detail: { finalized, type, filename, resolve, reject } */
  GET_MESH: 'as-get-configurator-mesh',
  /** Internal. Request a temporary URL for a mesh. Detail: { type, filename, finalized, resolve, reject } */
  GET_MESH_URL: 'as-intrn-get-mesh-url',
  /** Dispatched when a transferred mesh is applied from store. Detail: { mesh, meshId, stl }.
   *  Suppressed when the app set as-app.onTransferMeshLoaded — the callback replaces the event. */
  DB_MESH_LOADED: 'as-db-mesh-loaded',
  /** Confirm a received transfer mesh was processed. Detail: { meshId, shouldDelete?, error? }.
   *  error marks a failure resolution: cleanup runs but the success data_import
   *  log is skipped (the failure was already logged as data_import_error). */
  MESH_RX_COMPLETED: 'as-mesh-rx-completed',
} as const;

/**
 * Unified data-I/O completion events. Caught by <as-app> and turned into server
 * logs (data-import / data-export). The detail carries a `subcategory`
 * ('local' | 'interapp' | 'grabcad') and a `fileInfo` array; the mesh-vs-file
 * distinction is conveyed by fileInfo[].fileType rather than separate events.
 * Failures ride the same events via detail.error, but land server-side under
 * the distinct data_import_error / data_export_error log types.
 */
export const AS_FILE_EVENTS = {
  /** A data import completed (or failed). Detail: { subcategory, fileInfo, info?, error? } */
  IMPORT: 'as-data-import',
  /** A data export completed (or failed). Detail: { subcategory, fileInfo?, assemblyId?, toApp?, error? } */
  EXPORT: 'as-data-export',
  /** Files were dropped on a drop zone. Dispatched on the zone element, bubbles
   *  and composed, so a `window` listener sees every zone. Detail:
   *  { files: AsFileHandle[], source: 'viewport' | 'app' }. `source` names the
   *  zone the drop landed on. */
  DROPPED: 'as-files-dropped',
  //TODO:@REMOVE
  /** @deprecated use `DROPPED` and check `detail.source === 'viewport'`. Still
   *  fired alongside it, with the same detail. */
  DROPPED_VIEWPORT: 'as-dropped-files',
  //TODO:@REMOVE
  /** @deprecated use `DROPPED` and check `detail.source === 'app'`. Still fired
   *  alongside it, with the same detail. */
  DROPPED_APP: 'as-app-dropped-files',
} as const;

export const AS_HISTORY_EVENTS = {
  /** Trigger an undo operation. */
  UNDO: 'as-undo',
  /** Trigger a redo operation. */
  REDO: 'as-redo',
  /** Clear the entire history stack. */
  CLEAR: 'as-history-clear',
  /** Dispatched when an undo starts. Detail: { action } */
  UNDO_START: 'as-undo-start',
  /** Dispatched when a redo starts. Detail: { action } */
  REDO_START: 'as-redo-start',
  /** Request for batch start. Detail: { label, tags } */
  BATCH_START: 'as-history-batch-start',
  /** Request for batch end. */
  BATCH_END: 'as-history-batch-end',
  /** Request for batch cancel. */
  BATCH_CANCEL: 'as-history-batch-cancel',
} as const;

export const AS_SPOTLIGHT_EVENTS = {
  /** Dispatched by <as-spotlight> when it opens. Detail: { targets: Element[] } */
  SHOWN: 'as-spotlight-shown',
  /** Dispatched when it closes, however it was closed. */
  HIDDEN: 'as-spotlight-hidden',
  /** The user clicked the dimmed area rather than the highlighted element.
   *  Fired whether or not the spotlight is dismissible, so a walkthrough can
   *  use the click to step forward. */
  OUTSIDE_CLICK: 'as-spotlight-outside-click',
  /** The highlighted element left the page or stopped being drawn. The
   *  spotlight closes itself straight after, rather than leave a layer with
   *  no hole in it over a page nobody can click. */
  TARGET_LOST: 'as-spotlight-target-lost',
} as const;

/** Aggregate of all event names for type-safety and easy lookups. */
export const AS_EVENTS = {
  APP: AS_APP_EVENTS,
  PROJECT: AS_PROJECT_EVENTS,
  CONFIGURATOR: AS_CONFIGURATOR_EVENTS,
  MESH: AS_MESH_EVENTS,
  FILE: AS_FILE_EVENTS,
  HISTORY: AS_HISTORY_EVENTS,
  SPOTLIGHT: AS_SPOTLIGHT_EVENTS,
} as const;

/** Union of all event name string values. */
export type AsEventName =
  | typeof AS_APP_EVENTS[keyof typeof AS_APP_EVENTS]
  | typeof AS_PROJECT_EVENTS[keyof typeof AS_PROJECT_EVENTS]
  | typeof AS_CONFIGURATOR_EVENTS[keyof typeof AS_CONFIGURATOR_EVENTS]
  | typeof AS_MESH_EVENTS[keyof typeof AS_MESH_EVENTS]
  | typeof AS_FILE_EVENTS[keyof typeof AS_FILE_EVENTS]
  | typeof AS_HISTORY_EVENTS[keyof typeof AS_HISTORY_EVENTS]
  | typeof AS_SPOTLIGHT_EVENTS[keyof typeof AS_SPOTLIGHT_EVENTS];

// ============================================================================
// Dispatching
// ============================================================================

/** What sendEv can dispatch on. A real EventTarget, or anything that just
 *  declares dispatchEvent — a test double standing in for an element only
 *  promises that much. */
export type EvTarget = EventTarget | { dispatchEvent(ev: Event): boolean };

/** Options for sendEv. Same fields as CustomEventInit, with our defaults. */
export interface SendEvOptions<T = any> {
  /** The event's `detail` payload. */
  detail?: T;
  /** Defaults to true for an element, false for window/document — nothing is
   *  above them to bubble to. */
  bubbles?: boolean;
  /** Defaults to whatever `bubbles` ends up being. See the note below on why. */
  composed?: boolean;
  /** Lets a listener veto with preventDefault(); sendEv then returns false. */
  cancelable?: boolean;
}

/**
 * Dispatch a CustomEvent, with the defaults this library actually wants.
 *
 *   sendEv(this, 'as-new-session');
 *   sendEv(this, 'as-project-title-change', { detail: { name } });
 *   sendEv(window, 'as-appinfo-ready', { detail: appInfo });
 *   sendEv('#panel', 'as-notify', { detail: { message } });   // CSS selector
 *
 * Replaces `target.dispatchEvent(new CustomEvent(name, { bubbles: true,
 * composed: true, detail }))`, which is how nearly every one of these is
 * written by hand.
 *
 * **`composed` defaults to `bubbles`, and that is the point of the helper.**
 * An event dispatched from inside a shadow root with `bubbles: true` but
 * `composed: false` stops dead at the shadow boundary — it never reaches a
 * listener on `<as-app>` or `window`. Nothing warns you; the listener simply
 * never fires. Most of our components live in a shadow root, so "bubbles"
 * almost always means "and out of here too". Pass `composed: false` explicitly
 * when you really do want an event contained within its own tree.
 *
 * `bubbles` itself defaults to false on window and document, where there is
 * nothing above to bubble to, and true on an element.
 *
 * An omitted `detail` arrives as `null`, which is what a plain
 * `new CustomEvent(name)` gives you.
 *
 * Returns what `dispatchEvent` returns: false if a listener called
 * preventDefault() on a `cancelable` event, true otherwise.
 */
export function sendEv<T = any>(
  target: EvTarget | string | null | undefined,
  name: AsEventName | (string & {}),
  options: SendEvOptions<T> = {}
): boolean {
  // A null target is the `asApp()?.dispatchEvent(...)` case: the caller already
  // knows the element might not be there and wants nothing to happen. Silent.
  // A selector that matches nothing is different — that one is usually a typo,
  // so it warns.
  if (target == null) return false;
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) {
    console.warn(`[sendEv] target not found: ${target}`);
    return false;
  }
  const bubbles = options.bubbles ?? !(el === window || el === document);
  return el.dispatchEvent(new CustomEvent(name, {
    detail: options.detail,
    bubbles,
    composed: options.composed ?? bubbles,
    cancelable: options.cancelable ?? false,
  }));
}
